import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { observer } from "mobx-react";

import classNames from "classnames";

import { useRootAppStore } from "@hooks";
import { MessageContainer, SendMessage } from "@features/chat/desktop";
import {
	ExpandedMessageObject,
} from "@data-types/chat";
import ChatReplyScrollingLoader from "./ChatReplyScrollingLoader";
import { chatScrollToBottom } from "@utils/chat";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";

export default observer(function ChatMessagesWrapper() {
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const messageRef = useRef<HTMLDivElement>(null);
	const scrollingRef = useRef(null);

	const [scrollPosition, setScrollPosition] = useState(0);

	const { t } = useTranslation();

	const {
		chatViewStore: {
			chatChannelHandler,
			replyScrollingLoader,
			sharedBetSlipData,
			selectedMessageToReplyOn,
		},
	} = useRootAppStore();

	useEffect(() => {
		if (
			chatContainerRef.current &&
			messageRef.current &&
			chatContainerRef.current.scrollTop != 0
		) {
			const messageHeight = messageRef.current.clientHeight;
			chatContainerRef.current.scrollBy({
				top: -messageHeight,
				behavior: "auto",
			});
		}
	}, [chatChannelHandler.messageList.length]);

	useEffect(() => {
		setScrollPosition(0);
	}, [chatChannelHandler.activeChannelInfo]);

	if (!chatChannelHandler.messagesPaginationObject) {
		return null;
	}

	const { onNextPage, hasNextPage } =
		chatChannelHandler.messagesPaginationObject;

	if (onNextPage === undefined || hasNextPage === undefined) {
		return null;
	}

	return (
		<>
			<ErrorBoundaryWithPropagation
				componentName={"Desktop/ChatMessagesWrapper.tsx"}
			>
				<ChatReplyScrollingLoader />

				<div
					ref={chatContainerRef}
					id="scrollableDiv"
					className={classNames(
						"chat__box__container scrollbar--themed",
						{
							"chat__container--scrolling": replyScrollingLoader,
						}
					)}
					aria-disabled={replyScrollingLoader}
				>
					<InfiniteScroll
						ref={scrollingRef}
						onScroll={(e: any) => {
							setScrollPosition(e.target.scrollTop);
						}}
						className="chat__scroll"
						dataLength={chatChannelHandler.messageList.length}
						next={onNextPage}
						hasMore={hasNextPage}
						inverse={true}
						loader={
							<div className="chat__box__loader">
								<div className="loader__spinner">
									<div className="loader__spinner__wrapper"></div>
								</div>
							</div>
						}
						scrollableTarget="scrollableDiv"
						endMessage={
							<span className="chat__no-more">
								{t("CHAT.NO_MORE_MESSAGES_AVAILABLE")}
							</span>
						}
					>
						<div id="bottom-scroll-div"></div>

						{chatChannelHandler.messageList
							.reverse()
							.map((message) => {
								return (
									<div
										key={message.messageId}
										ref={messageRef}
									>
										<MessageContainer
											message={
												message as ExpandedMessageObject
											}
										/>
									</div>
								);
							})}
						<div id="reply-scroll-div"></div>
					</InfiniteScroll>
				</div>

				{scrollPosition < 0 &&
					!replyScrollingLoader &&
					!selectedMessageToReplyOn &&
					!sharedBetSlipData && (
						<button
							type="button"
							className="chat__btn--scroll btn btn--round btn--round--sml btn--icon btn--tertiary"
							onClick={() => chatScrollToBottom()}
						>
							<i className="is-dark u-icon u-icon--arrow--down u-icon--tny" />
						</button>
					)}

				<SendMessage />
			</ErrorBoundaryWithPropagation>
		</>
	);
});
